import React, { useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { useStoredSettings, useStoredSettingsListener, useLocalStorageReader } from 'cityads-v3/hooks/storedSettings';
import classes from './ViewSelector.scss'

import AppsIcon from '@mui/icons-material/AppsOutlined'
import IconButton, { IconButtonProps } from '@mui/material/IconButton'
import Tooltip from 'cityads-v3/components/Tooltip/Tooltip'
import ViewHeadlineOutlinedIcon from '@mui/icons-material/ViewHeadlineOutlined';
import ViewListIcon from '@mui/icons-material/ViewList';

export type ViewVariants = 'list' | 'table' | 'cards'
type ChangeViewSelector = (variant: ViewVariants) => IconButtonProps['onClick'];

type ViewSelectorProps = Omit<React.HTMLAttributes<HTMLDivElement>, 'onChange'> & {
  onChange: ChangeViewSelector
  variants?: ViewVariants[]
  value: ViewVariants
}

export default function ViewSelector({
  onChange,
  variants = ['list', 'table'],
  value,
  ...rest
}: ViewSelectorProps): JSX.Element {
  const { t } = useTranslation(['main']);


  return (
    <div {...rest}>
      {variants.includes('list') && (
        <Tooltip
          title={t('viewModeList')}
        >
          <IconButton
            size="large"
            onClick={onChange('list')}
            color={value === 'list' ? 'primary' : 'default'}
            classes={{
              root: classes.iconButton,
              colorPrimary: classes.iconButtonPrimary,
            }}
          >
            <ViewHeadlineOutlinedIcon />
          </IconButton>
        </Tooltip>

      )}

      {variants.includes('table') && (
        <Tooltip
          title={t('viewModeTable')}
        >
          <IconButton
            size="large"
            onClick={onChange('table')}
            color={value === 'table' ? 'primary' : 'default'}
            classes={{
              root: classes.iconButton,
              colorPrimary: classes.iconButtonPrimary,
            }}
          >
            <ViewListIcon />
          </IconButton>
        </Tooltip>
      )}

      {variants.includes('cards') && (
        <Tooltip
          title={t('viewModeCards')}
        >
          <IconButton
            size="large"
            onClick={onChange('cards')}
            color={value === 'cards' ? 'primary' : 'default'}
            classes={{
              root: classes.iconButton,
              colorPrimary: classes.iconButtonPrimary,
            }}
          >
            <AppsIcon />
          </IconButton>
        </Tooltip>
      )}
    </div>
  )
}

export function useViewSelector(key: string, defaultValue: ViewVariants = 'list'): [ViewVariants, ChangeViewSelector] {
  const [value, setValue] = useStoredSettings(key, defaultValue);
  useLocalStorageReader(key)
  useStoredSettingsListener(key)

  const handleChange = useCallback((value: ViewVariants) => () => {
    setValue(value);
  }, [setValue])

  return [
    value,
    handleChange
  ]
}
